import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API } from '../App';
import { usePersistedState, STORAGE_KEYS } from '../hooks/usePersistedState';

const IslandContext = createContext();

// Helper to get island from URL - runs synchronously
const getIslandFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlIsland = urlParams.get('island');
  if (urlIsland && ['curacao', 'aruba', 'bonaire'].includes(urlIsland.toLowerCase())) {
    return urlIsland.toLowerCase();
  }
  return null;
};

export const useIsland = () => {
  const context = useContext(IslandContext);
  if (!context) {
    throw new Error('useIsland must be used within an IslandProvider');
  }
  return context;
};

export const IslandProvider = ({ children }) => {
  const [islands, setIslands] = useState([]);
  
  // Get URL island synchronously on first render
  const urlIslandInitial = useMemo(() => getIslandFromUrl(), []);
  
  // Use persisted state for island selection
  // If URL has island param, use initialValue option to override localStorage
  const [currentIsland, setCurrentIslandRaw] = usePersistedState(
    STORAGE_KEYS.ISLAND, 
    'curacao',
    { initialValue: urlIslandInitial } // URL param takes priority
  );
  const [showIslandSelector, setShowIslandSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetecting, setIsDetecting] = useState(false);

  // Sync with URL parameter 'island' on URL changes (not on mount since we handle that above)
  useEffect(() => {
    const syncFromUrl = () => {
      const urlIsland = getIslandFromUrl();
      if (urlIsland && urlIsland !== currentIsland) {
        setCurrentIslandRaw(urlIsland);
      }
    };
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [currentIsland, setCurrentIslandRaw]);

  // Fetch all islands
  const fetchIslands = useCallback(async () => {
    try {
      const response = await fetch(`${API}/islands`);
      if (response.ok) {
        const data = await response.json();
        setIslands(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching islands:', error);
    }
    return [];
  }, []);

  // Detect user's island via IP geolocation
  const detectIsland = useCallback(async () => {
    setIsDetecting(true);
    try {
      const response = await fetch(`${API}/geolocation/detect`);
      if (response.ok) {
        const data = await response.json();
        if (data.detected && data.island) {
          return data.island;
        }
      }
    } catch (error) {
      console.error('Error detecting island:', error);
    } finally {
      setIsDetecting(false);
    }
    return null;
  }, []);

  // Select an island (persisted automatically via hook) and update URL
  const selectIsland = useCallback((islandCode) => {
    setCurrentIslandRaw(islandCode);
    setShowIslandSelector(false);
    
    // Update URL with new island parameter (if on marketplace page)
    const currentPath = window.location.pathname;
    if (currentPath.includes('/marketplace') || currentPath.includes('/categories')) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('island', islandCode);
      const newUrl = `${currentPath}?${urlParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [setCurrentIslandRaw]);

  // Get locations for specific islands
  const getLocationsForIslands = useCallback((islandCodes) => {
    if (!islandCodes || islandCodes.length === 0) {
      return islands.flatMap(island => island.locations || []);
    }
    return islands
      .filter(island => islandCodes.includes(island.code))
      .flatMap(island => island.locations || []);
  }, [islands]);

  // Get current island data
  const getCurrentIslandData = useCallback(() => {
    return islands.find(island => island.code === currentIsland);
  }, [islands, currentIsland]);

  // Initialize on mount - only detect if no saved preference
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      
      // Fetch islands first
      const fetchedIslands = await fetchIslands();
      
      // If currentIsland is already set from persisted state and is valid, we're done
      if (currentIsland && fetchedIslands.some(i => i.code === currentIsland)) {
        setIsLoading(false);
        return;
      }
      
      // No saved preference or invalid - try to detect island
      const detectedIsland = await detectIsland();
      
      if (detectedIsland && fetchedIslands.some(i => i.code === detectedIsland)) {
        setCurrentIslandRaw(detectedIsland);
      } else {
        // Default to Curaçao
        setCurrentIslandRaw('curacao');
      }
      
      setIsLoading(false);
    };

    initialize();
  }, [fetchIslands, detectIsland, currentIsland, setCurrentIslandRaw]);

  const value = {
    islands,
    currentIsland,
    setCurrentIsland: selectIsland,
    showIslandSelector,
    setShowIslandSelector,
    isLoading,
    isDetecting,
    getLocationsForIslands,
    getCurrentIslandData,
    fetchIslands
  };

  return (
    <IslandContext.Provider value={value}>
      {children}
    </IslandContext.Provider>
  );
};

export default IslandContext;
